import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EntityDetailDrawer from '../../EntityDetailDrawer';
import { BulletList, Code, Field, Section } from '../../Ontology/DetailPrimitives';
import {
    BenchmarkRequestError, BenchmarkRun, SuppliedAlertRow, SuppliedKycRow, benchmarkService
} from '../../../services/benchmarkService';
import { count } from '../BenchmarkVocabulary';
import ColumnMappingTable from './ColumnMappingTable';
import { downloadText, toCsv } from './csvExport';
import { RETENTION_NOTE, alertsToCases, sampleName } from './suppliedSampleCopy';
import { dsVar, ink } from '../../../theme/tokens';

/** Rows fetched per page when exporting; the endpoint's own cap. */
const EXPORT_PAGE = 200;

/** Every page of a run's supplied sample. */
async function fetchWholeSample(runKey: number) {
    const alerts: SuppliedAlertRow[] = [];
    let kyc: SuppliedKycRow[] = [];
    for (let offset = 0; ; offset += EXPORT_PAGE) {
        const page = await benchmarkService.getRunSample(runKey, { limit: EXPORT_PAGE, offset });
        alerts.push(...(page?.alerts ?? []));
        // The KYC comes whole with every page; the first is enough.
        if (offset === 0) kyc = page?.kyc ?? [];
        const total = page?.pagination?.total ?? 0;
        if (!page?.alerts?.length || alerts.length >= total) break;
    }
    return { alerts, kyc };
}

/** Back into the loader's own column names, so an export can be resubmitted. */
const alertCsvRow = (a: SuppliedAlertRow) => ({
    alert_ref: a.alertRef,
    customer_ref: a.customerRef,
    scenario_id: a.scenarioId,
    config_epoch: a.configEpoch,
    opened_at: a.openedAt,
    priority: a.priority,
    priority_source: a.prioritySource,
    disposition: a.disposition,
    disposition_raw: a.dispositionRaw,
    sar_filed: a.sarFiled,
    alert_score: a.alertScore,
    trigger_features: a.features,
    context_features: a.contextFeatures
});

const kycCsvRow = (k: SuppliedKycRow) => ({ customer_ref: k.customerRef, kyc_as_of: k.kycAsOf, ...k.profile });

/**
 * How a supplied sample was read: the column mapping, what was left unused,
 * where each customer's KYC came from and how it was dated, and the loader's
 * warnings verbatim.
 *
 * Docked beside the results, so the reader can check a figure against the
 * data behind it without leaving the page.
 */
const ProvenanceDrawer: React.FC<{
    open: boolean;
    run: BenchmarkRun | null;
    onClose: () => void;
}> = ({ open, run, onClose }) => {
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const source = run?.source;
    if (!run || source?.dataSource !== 'user_supplied') {
        return <EntityDetailDrawer open={false} onClose={onClose} title="" description="" docked dockedMinWidth={1280} />;
    }

    const kyc = source.kyc;
    const mapping = source.columnMapping ?? [];
    const byModel = mapping.filter((m) => m.method === 'llm').length;

    const exportSample = async () => {
        setExporting(true);
        setExportError(null);
        try {
            const { alerts, kyc: kycRows } = await fetchWholeSample(run.runKey);
            const stem = `run-${run.runKey}-supplied`;
            downloadText(`${stem}-alerts.csv`, toCsv(alerts.map(alertCsvRow)));
            if (kycRows.length) downloadText(`${stem}-kyc.csv`, toCsv(kycRows.map(kycCsvRow)));
        } catch (err) {
            setExportError(err instanceof BenchmarkRequestError ? err.message : 'Could not export this sample.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<FactCheckOutlinedIcon />}
            subtitle="Supplied sample"
            title={sampleName(source.sourceName)}
            description={`${alertsToCases(source.rowsSupplied, run.predictionCount)}. How the file was read, and what the run could and could not use from it.`}
            actions={
                <Box sx={{ width: '100%' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={exportSample}
                        disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, color: dsVar('accent-fg'), borderColor: 'rgba(255, 222, 89, 0.5)',
                            '&:hover': { borderColor: dsVar('accent-fg'), backgroundColor: 'rgba(255, 222, 89, 0.08)' }
                        }}
                    >
                        {exporting ? 'Exporting…' : 'Export sample as CSV'}
                    </Button>
                    {exportError && (
                        <Typography sx={{ fontSize: '0.75rem', color: ink('#FCA5A5'), mt: 1 }}>{exportError}</Typography>
                    )}
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 1, lineHeight: 1.5 }}>
                        {RETENTION_NOTE}
                    </Typography>
                </Box>
            }
        >
            <Section title="Column mapping">
                {byModel > 0 && (
                    <Typography sx={{ fontSize: '0.8125rem', color: ink('#FBBF24'), mb: 1.25, lineHeight: 1.6 }}>
                        {byModel} {byModel === 1 ? 'column was' : 'columns were'} identified by the model. Check {byModel === 1 ? 'it' : 'them'} before relying on these results.
                    </Typography>
                )}
                <ColumnMappingTable entries={mapping} />
            </Section>

            <Section title="Not used" always>
                {(source.unmappedColumns?.length ?? 0) === 0 && (source.unmappedDispositions?.length ?? 0) === 0 ? (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>Every column and outcome wording was understood.</Typography>
                ) : (
                    <>
                        <Field
                            label="Columns"
                            value={source.unmappedColumns?.length
                                ? <Box className="flex flex-wrap gap-2">{source.unmappedColumns.map((c) => <Code key={c}>{c}</Code>)}</Box>
                                : undefined}
                        />
                        <Field
                            label="Outcome wordings not understood"
                            value={source.unmappedDispositions?.length
                                ? <Box className="flex flex-wrap gap-2">{source.unmappedDispositions.map((d) => <Code key={d}>{d}</Code>)}</Box>
                                : undefined}
                        />
                    </>
                )}
            </Section>

            <Section title="KYC" always>
                {!kyc ? (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.6 }}>
                        No KYC was supplied. Every customer's KYC was read from your customer records, which are revised in place, so it may include changes made after the alert.
                    </Typography>
                ) : (
                    <>
                        <Field label="File" value={kyc.sourceName ? sampleName(kyc.sourceName) : undefined} />
                        <Field label="Records supplied" value={kyc.rowsSupplied !== null ? count(kyc.rowsSupplied) : undefined} />
                        <Field
                            label="Where each customer's KYC came from"
                            value={`${count(kyc.suppliedCustomers ?? 0)} from the supplied file · ${count(kyc.customerMasterCustomers ?? 0)} from your customer records`}
                        />
                        <Field
                            label="How the supplied KYC was dated"
                            value={`${count(kyc.pointInTime ?? 0)} before the customer's first alert · ${count(kyc.postAlert ?? 0)} after it · ${count(kyc.undated ?? 0)} undated`}
                        />
                        {(kyc.postAlert ?? 0) > 0 && (
                            <Typography sx={{ fontSize: '0.8125rem', color: ink('#FBBF24'), lineHeight: 1.6 }}>
                                Some KYC was dated after the alert and may reflect the investigation's outcome.
                            </Typography>
                        )}
                    </>
                )}
            </Section>

            <Section title="Warnings when the sample was loaded" always>
                {source.warnings?.length
                    ? <BulletList items={source.warnings} />
                    : <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>None.</Typography>}
            </Section>

            {source.contentFingerprint && (
                <Section title="Fingerprint">
                    <Code>{source.contentFingerprint}</Code>
                </Section>
            )}
        </EntityDetailDrawer>
    );
};

export default ProvenanceDrawer;
